import './styles.scss';

import { ReactElement, useEffect, useMemo } from 'react';

import CustomPdfTable from '@/features/PdfLayout/layouts/Invoice/CustomPdfTable';
import { InvoiceFormatEnum } from '@/models/gen/graphql';
import InvoicePdfClient from '@/features/PdfLayout/layouts/Invoice/InvoicePdfClient';
import InvoicePdfCompany from '@/features/PdfLayout/layouts/Invoice/InvoicePdfCompany';
import InvoicePdfDates from '@/features/PdfLayout/layouts/Invoice/InvoicePdfDates';
import InvoicePdfFooterAddress from '@/features/PdfLayout/layouts/Invoice/InvoicePdfFooterAddress';
import InvoicePdfFooterContact from '@/features/PdfLayout/layouts/Invoice/InvoicePdfFooterContact';
import InvoicePdfFooterWeb from '@/features/PdfLayout/layouts/Invoice/InvoicePdfFooterWeb';
import InvoicePdfMetrics from '@/features/PdfLayout/layouts/Invoice/InvoicePdfMetrics';
import InvoicePdfTable from '@/features/PdfLayout/layouts/Invoice/InvoicePdfTable';
import InvoicePdfTotals from '@/features/PdfLayout/layouts/Invoice/InvoicePdfTotals';
import SkyhopPdfFooter from '@/features/PdfLayout/components/SkyhopPdfFooter';
import SkyhopPdfHeader from '@/features/PdfLayout/components/SkyhopPdfHeader';
import { useClientIdsThatRequireCrew } from '@/api/services/providers/searchProviders';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useGetInvoicePreview } from '@/api/services/invoices';
import useLocalStorage from '@/hooks/useLocalStorage';
import { usePdfLayout } from '@/features/PdfLayout';

export type InvoiceProps = { id: string | number };
const Invoice = ({ id }: InvoiceProps): ReactElement => {
  useDocumentTitle('Transportation Invoice');
  const [error, data, details, clientIdsThatRequireCrew] = useInvoicePdfData(id);

  // const totalLegalCombinedTrips = data?.output?.trips?.length || 0;
  const requiresNames = clientIdsThatRequireCrew?.includes(data?.output?.invoice?.payerProviderId);
  const showCrewId = data?.output?.invoice?.format === InvoiceFormatEnum.Crew;

  if (error) return <div>Error loading invoice preview.</div>;
  return (
    <div className="InvoicePdf">
      <SkyhopPdfHeader>
        Transportation
        <br />
        Invoice
      </SkyhopPdfHeader>
      <div className="InvoicePdf-Content">
        <div className="InvoicePdf-Details">
          <InvoicePdfCompany />
          <InvoicePdfClient />
          <InvoicePdfDates />
          <InvoicePdfMetrics />
        </div>
        {data?.output?.trips?.length > 0 && (
          <InvoicePdfTable combineIds={details.combineIds} requiresNames={requiresNames} showCrewId={showCrewId} />
        )}
        {data?.output?.customCharges?.length > 0 && <CustomPdfTable />}
        <InvoicePdfTotals />
      </div>
      <SkyhopPdfFooter title="Office Contact">
        <InvoicePdfFooterAddress />
        <InvoicePdfFooterContact />
        <InvoicePdfFooterWeb />
      </SkyhopPdfFooter>
    </div>
  );
};

const useInvoicePdfData = (invoiceId: string | number) => {
  // Invoice Data
  const [{ data = {}, loading: loadingInvoice, error }, { refetch }] = useGetInvoicePreview();
  // Pdf Layout Loading State
  const [loadingLayout, setState] = usePdfLayout(({ state: { loading }, setState }) => [loading, setState]);
  // Crew providers
  const [{ data: clientIdsThatRequireCrew, loading: loadingCrewProviders }] = useClientIdsThatRequireCrew();

  // All Airports from Local Storage
  const [storedAirports] = useLocalStorage('airports', { items: [] });
  const allAirports = useMemo(
    () =>
      storedAirports?.items
        ?.filter((airport) => !airport?.group && !!airport?.value)
        ?.map((airport) => airport?.value)
        ?.join?.(', ') || '',
    [storedAirports]
  );
  // Combine IDs from Trips
  const combineIds = useMemo(() => {
    const ids = [];
    data?.output?.trips?.forEach?.((trip) => {
      if (trip?.combineId && !ids.includes(trip.combineId)) ids.push(trip.combineId);
    });
    return ids;
  }, [data?.output?.trips]);

  // Memoized Details
  const details = useMemo(
    () => ({
      allAirports,
      combineIds,
    }),
    [allAirports, combineIds]
  );

  useEffect(() => {
    // Fetch Invoice Data when invoiceId changes
    const id = Number(invoiceId);
    if (!id || isNaN(id) || loadingInvoice) return;

    refetch(id).then((res) => {
      res.output.company.invoiceEmail = 'billing@skyhopglobal.com'; // TODO: Remove this line. This is a temporary fix until BE fixes email in response
      // Delay applying data so defaults render first (dev/stage only — prod renders immediately)
      setState((current) => ({ ...current, data: res }));
    });
    /* PREVENT RE-RENDERS
      loadingInvoice, refetch, setState may cause unwanted re-renders
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);
  useEffect(() => {
    // Sync loading state between layout and invoice data
    const isLoading = loadingInvoice || loadingCrewProviders;
    if (loadingLayout === isLoading) return;
    setState((current) => ({ ...current, loading: isLoading }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInvoice, loadingCrewProviders]);
  useEffect(() => {
    // Sync the GQL error from useGetInvoicePreview directly to shared layout state.
    // This is kept separate from the loading effect so the loading guard cannot block it.
    setState((current) => ({ ...current, error }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return [error, data, details, clientIdsThatRequireCrew];
};

export default Invoice;
